import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    eventType: '',
    eventDate: '',
    guestCount: '',
    location: '',
    message: ''
  });
  const [acceptTerms, setAcceptTerms] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      eventType: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!acceptTerms) {
      toast({
        title: 'Zustimmung erforderlich',
        description: 'Bitte akzeptieren Sie die AGB und Datenschutzerklärung, um fortzufahren.',
        variant: 'destructive'
      });
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'Anfrage gesendet!',
          description: data.message || 'Vielen Dank für Ihre Anfrage. Wir melden uns schnellstmöglich bei Ihnen.',
        });

        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          eventType: '',
          eventDate: '',
          guestCount: '',
          location: '',
          message: ''
        });
        setAcceptTerms(false);
      } else {
        toast({
          title: 'Fehler',
          description: data.detail || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: 'Fehler',
        description: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
        variant: 'destructive'
      });
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Adresse',
      content: 'Ulm & Umgebung\nBaden-Württemberg'
    },
    {
      icon: Phone,
      title: 'Telefon',
      content: '+49 173 6338684\nMo-Fr: 9:00 - 18:00 Uhr'
    },
    {
      icon: Mail,
      title: 'E-Mail',
      content: 'info@umulmevents.de\nkontakt@umulmevents.de'
    },
    {
      icon: Clock,
      title: 'Erreichbarkeit',
      content: 'Mo-Fr: 9:00 - 18:00 Uhr\nSa: Nach Vereinbarung'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1920&h=1080&fit=crop)',
            filter: 'brightness(0.4)'
          }}
        />
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            Kontakt & <span className="text-red-600">Buchung</span>
          </h1>
          <p className="text-xl md:text-2xl font-light">
            Wir freuen uns auf Ihre Anfrage
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="bg-red-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-red-600" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 text-gray-900">{info.title}</h3>
                    <p className="text-gray-600 whitespace-pre-line text-sm">{info.content}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Anfrage <span className="text-red-600">senden</span>
            </h2>
            <p className="text-xl text-gray-600">
              Füllen Sie das Formular aus und wir melden uns innerhalb von 24 Stunden
            </p>
          </div>

          <Card className="border-0 shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">Vorname *</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Nachname *</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail Adresse *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefonnummer *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company">Firma (optional)</Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="eventType">Veranstaltungstyp *</Label>
                  <Select value={formData.eventType} onValueChange={handleSelectChange} required>
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Bitte wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hochzeit">Hochzeit</SelectItem>
                      <SelectItem value="firmenfeier">Firmenfeier</SelectItem>
                      <SelectItem value="geburtstag">Geburtstag</SelectItem>
                      <SelectItem value="festival">Festival</SelectItem>
                      <SelectItem value="private-party">Private Party</SelectItem>
                      <SelectItem value="andere">Andere</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="eventDate">Veranstaltungsdatum *</Label>
                    <Input
                      id="eventDate"
                      name="eventDate"
                      type="date"
                      value={formData.eventDate}
                      onChange={handleChange}
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guestCount">Ungefähre Personenzahl *</Label>
                    <Input
                      id="guestCount"
                      name="guestCount"
                      type="number"
                      value={formData.guestCount}
                      onChange={handleChange}
                      required
                      className="h-12"
                      placeholder="z.B. 100"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Veranstaltungsort *</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    className="h-12"
                    placeholder="z.B. Ulm, Neu-Ulm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Ihre Nachricht an uns</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    placeholder="Erzählen Sie uns mehr über Ihre Veranstaltung..."
                  />
                </div>

                <div className="flex items-start gap-3 pt-2">
                  <Checkbox
                    id="acceptTerms"
                    checked={acceptTerms}
                    onCheckedChange={(checked) => setAcceptTerms(checked === true)}
                    className="mt-1"
                    data-testid="accept-terms-checkbox"
                  />
                  <Label
                    htmlFor="acceptTerms"
                    className="text-sm text-gray-700 leading-relaxed cursor-pointer font-normal"
                  >
                    Ich habe die{' '}
                    <Link
                      to="/agb"
                      target="_blank"
                      className="text-red-600 hover:underline font-medium"
                      data-testid="agb-link"
                    >
                      AGB
                    </Link>
                    {' '}und die{' '}
                    <Link
                      to="/datenschutz"
                      target="_blank"
                      className="text-red-600 hover:underline font-medium"
                      data-testid="datenschutz-link"
                    >
                      Datenschutzerklärung
                    </Link>
                    {' '}gelesen und akzeptiere diese. *
                  </Label>
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={!acceptTerms}
                    className="w-full bg-red-600 hover:bg-red-700 text-white h-14 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-contact-form-btn"
                  >
                    Anfrage absenden
                  </Button>
                </div>

                <p className="text-sm text-gray-500 text-center">
                  * Pflichtfelder - Wir nutzen Ihre Daten nur zur Bearbeitung Ihrer Anfrage
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Unser <span className="text-red-600">Servicegebiet</span>
            </h2>
            <p className="text-xl text-gray-600">
              Wir sind in Ulm, Neu-Ulm und der gesamten Umgebung für Sie da
            </p>
          </div>
          <div className="bg-gray-200 rounded-lg h-96 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-16 h-16 text-red-600 mx-auto mb-4" />
              <p className="text-xl font-semibold text-gray-700">Servicegebiet: Ulm & Umgebung</p>
              <p className="text-gray-600 mt-2">Ulm • Neu-Ulm • Blaustein • Senden • Ehingen • Laupheim</p>
              <p className="text-gray-600">Illertissen • Günzburg • und weitere Orte</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;